import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase.js';
import { Icon } from './Icon.js';
import JellyfishLoader from './JellyfishLoader';
import { dedupeRequest, getCacheKey } from '../utils/queryDeduplication';
import { evaluateChallengeTarget } from '../utils/challengeTarget';
import './ChallengesTab.css';

function ChallengesTab({ userId, isOwnProfile = true }) {
  const [challenges, setChallenges] = useState([]);
  const [userProgress, setUserProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active'); // all, active, completed
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [pendingChallengeId, setPendingChallengeId] = useState(null);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [disclaimerText, setDisclaimerText] = useState('');
  const [disclaimerVersion, setDisclaimerVersion] = useState('');
  const [acceptResponsibility, setAcceptResponsibility] = useState(false);
  const [doNotShowAgain, setDoNotShowAgain] = useState(false);

  useEffect(() => {
    loadChallengesData();
  }, [userId]);

  const loadChallengesData = async () => {
    try {
      setLoading(true);
      
      // Get userId - use prop if available, otherwise get from auth (getSession to avoid duplicate GET /auth/v1/user)
      let id = userId;
      if (!id) {
        const { data: { session } } = await supabase.auth.getSession();
        const user = session?.user;
        if (!user) {
          // No user authenticated, just load challenges without progress
          const cacheKey = getCacheKey('loadChallenges', 'anonymous');
          const challengesData = await dedupeRequest(cacheKey, async () => {
            const { data, error } = await supabase
              .from('challenges')
              .select(`
                *,
                challenge_targets (*)
              `)
              .order('start_date', { ascending: false });

            if (error) throw error;
            return data;
          });
          setChallenges(challengesData || []);
          setUserProgress([]);
          return;
        }
        id = user.id;
      }
      
      // Use deduplication for challenges fetch
      const challengesCacheKey = getCacheKey('loadChallenges', id || 'all');
      const challengesData = await dedupeRequest(challengesCacheKey, async () => {
        // Load all challenges
        const { data, error } = await supabase
          .from('challenges')
          .select(`
            *,
            challenge_targets (*)
          `)
          .order('start_date', { ascending: false });

        if (error) throw error;
        return data;
      });
      setChallenges(challengesData || []);

      // Load user's progress on challenges (only if we have a valid userId)
      if (id) {
        const progressCacheKey = getCacheKey('loadUserChallengeProgress', id);
        const progressData = await dedupeRequest(progressCacheKey, async () => {
          const { data, error } = await supabase
            .from('user_challenge_progress')
            .select('*')
            .eq('user_id', id);

          if (error) throw error;
          return data;
        });
        setUserProgress(progressData || []);
      } else {
        setUserProgress([]);
      }

    } catch (error) {
      console.error('Error loading challenges data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getUserProgressForChallenge = (challengeId) => {
    return userProgress.find(p => p.challenge_id === challengeId);
  };

  const getChallengeStatus = (challenge) => {
    const now = new Date();
    const startDate = challenge.start_date ? new Date(challenge.start_date) : null;
    const endDate = challenge.end_date ? new Date(challenge.end_date) : null;

    if (endDate && endDate < now) return 'ended';
    if (startDate && startDate > now) return 'upcoming';
    return 'active';
  };

  const getFilteredChallenges = () => {
    return challenges.filter(challenge => {
      const progress = getUserProgressForChallenge(challenge.id);
      const status = getChallengeStatus(challenge);

      if (filter === 'active') {
        return status === 'active' && progress && !progress.completed;
      }
      if (filter === 'completed') {
        return progress && progress.completed;
      }
      return true; // 'all'
    });
  };

  // Check if user has accepted the challenge join disclaimer
  // Returns true if user has accepted (don't show modal), false if should show modal
  // Checks the most recent record since we always insert new rows
  const checkDisclaimerAcceptance = async (userId) => {
    if (!userId) {
      return false;
    }
    try {
      const { data, error } = await supabase
        .from('disclaimers_log')
        .select('id, accepted_at, do_not_show_again')
        .eq('user_id', userId)
        .eq('disclaimer_type', 'challenge_join')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        return false; // Show modal on error
      }

      // Show modal if no record exists (user hasn't accepted yet)
      if (!data) {
        return false; // Should show modal
      }
      
      // Don't show modal only if do_not_show_again is true
      // Even if user accepted before, if they didn't check "do not show again", show modal again
      if (data.do_not_show_again === true) {
        return true; // Don't show modal
      }
      
      // Show modal if do_not_show_again is false or null
      return false; // Should show modal
    } catch (error) {
      return false; // Show modal on error
    }
  };

  // Fetch disclaimer text from disclaimers table
  const fetchDisclaimerText = async () => {
    try {
      const { data, error } = await supabase
        .from('disclaimers')
        .select('text, version')
        .eq('type', 'challenge_join')
        .eq('latest', true)
        .maybeSingle();
      
      if (error) {
        return null;
      }

      if (!data) {
        return null;
      }

      return data;
    } catch (error) {
      return null;
    }
  };

  // Store disclaimer acceptance - always inserts new row for audit trail
  const storeDisclaimerAcceptance = async (userId, version, doNotShow) => {
    if (!userId) return false;
    try {
      // Always insert a new record (creates audit trail)
      const { error } = await supabase
        .from('disclaimers_log')
        .insert({
          user_id: userId,
          disclaimer_type: 'challenge_join',
          disclaimer_version: version,
          accepted_at: new Date().toISOString(),
          do_not_show_again: doNotShow
        });

      if (error) {
        console.error('Error storing disclaimer acceptance:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error storing disclaimer acceptance:', error);
      return false;
    }
  };

  // Check if user has health data consent
  const checkHealthDataConsent = async (userId) => {
    if (!userId) return false;
    try {
      const { data, error } = await supabase
        .from('health_data_consent')
        .select('id')
        .eq('user_id', userId)
        .eq('consent_type', 'health_biometric_processing')
        .eq('is_active', true)
        .maybeSingle();

      if (error) {
        console.error('Error checking health data consent:', error);
        return false;
      }

      return !!data;
    } catch (error) {
      console.error('Error checking health data consent:', error);
      return false;
    }
  };

  // Store health data consent
  const storeHealthDataConsent = async (userId) => {
    if (!userId) return false;
    try {
      const consentText = "I explicitly consent to the processing of my health and biometric data, including wearable data and laboratory biomarkers (e.g. hormone levels), to provide personalised health insights and reports.";
      const consentTextVersion = "v1.0-2026-01-12";

      const { error } = await supabase
        .from('health_data_consent')
        .insert({
          user_id: userId,
          consent_type: 'health_biometric_processing',
          consent_text_version: consentTextVersion,
          consent_text_snapshot: consentText,
          given_at: new Date().toISOString()
        });

      if (error) {
        console.error('Error storing health data consent:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error storing health data consent:', error);
      return false;
    }
  };

  // Actual join challenge function (after consent check)
  const joinChallengeInternal = async (challengeId, id) => {
    // Find the challenge to get its details
    const challenge = challenges.find(c => c.id === challengeId);
    if (!challenge) {
      alert('Challenge not found');
      return;
    }

    // Calculate retroactive progress for biohack challenges
    let progressValue = 0;
    let completed = false;

    const { data: targets } = await supabase
      .from('challenge_targets')
      .select('*')
      .eq('challenge_id', challengeId);

    if (targets && targets.length > 0) {
      const target = targets[0];
      if (target.target_type === 'biohack' && target.aggregation === 'count') {
        // Use biohack_id directly from the challenge target
        if (target.biohack_id) {
          const { data } = await supabase
            .from('logging_hack_entries')
            .select('*')
            .eq('user_id', id)
            .eq('hack_id', target.biohack_id)
            .gte('date', challenge.start_date)
            .lte('date', challenge.end_date);

          progressValue = data ? data.length : 0;
          completed = evaluateChallengeTarget(progressValue, target.operator, target.target_value);
        }
      }
    }

    const { error } = await supabase
      .from('user_challenge_progress')
      .insert({
        user_id: id,
        challenge_id: challengeId,
        progress_value: progressValue,
        completed: completed,
        completed_at: completed ? new Date().toISOString() : null
      });

    if (error) throw error;

    // Reload data
    await loadChallengesData();
  };

  const handleJoinChallenge = async (challengeId) => {
    try {
      // Get userId - use prop if available, otherwise get from auth (getSession to avoid extra GET /auth/v1/user)
      let id = userId;
      if (!id) {
        const { data: { session } } = await supabase.auth.getSession();
        id = session?.user?.id;
        if (!id) {
          alert('You must be logged in to join a challenge.');
          return;
        }
      }

      // Check if user has accepted the disclaimer
      const hasAcceptedDisclaimer = await checkDisclaimerAcceptance(id);
      
      if (!hasAcceptedDisclaimer) {
        // Fetch disclaimer text and show modal
        const disclaimer = await fetchDisclaimerText();
        if (disclaimer && disclaimer.text) {
          setDisclaimerText(disclaimer.text);
          setDisclaimerVersion(disclaimer.version || 'v1.0');
          setPendingChallengeId(challengeId);
          setShowDisclaimerModal(true);
          setAcceptResponsibility(false);
          setDoNotShowAgain(false);
          return;
        }
      }

      // Check if user has health data consent
      const userHasConsented = await checkHealthDataConsent(id);
      
      if (!userHasConsented) {
        // Store challenge ID as pending and show consent modal
        setPendingChallengeId(challengeId);
        setShowConsentModal(true);
        return;
      }

      // User has accepted disclaimer and consented, proceed with joining
      await joinChallengeInternal(challengeId, id);
    } catch (error) {
      console.error('Error joining challenge:', error);
      alert('Error joining challenge. Please try again.');
    }
  };

  // Handle disclaimer acceptance
  const handleDisclaimerAccept = async () => {
    if (!acceptResponsibility) {
      // Required checkbox not checked
      return;
    }

    try {
      // Get userId - use prop if available, otherwise get from auth (getSession to avoid extra GET /auth/v1/user)
      let id = userId;
      if (!id) {
        const { data: { session } } = await supabase.auth.getSession();
        id = session?.user?.id;
        if (!id) {
          setShowDisclaimerModal(false);
          return;
        }
      }

      // Store disclaimer acceptance in database
      const acceptanceStored = await storeDisclaimerAcceptance(id, disclaimerVersion, doNotShowAgain);
      
      if (acceptanceStored) {
        setShowDisclaimerModal(false);
        setAcceptResponsibility(false);
        setDoNotShowAgain(false);
        
        // Continue with challenge join flow
        // Check health data consent next
        const userHasConsented = await checkHealthDataConsent(id);
        
        if (!userHasConsented && pendingChallengeId) {
          // Store challenge ID as pending and show consent modal
          setShowConsentModal(true);
        } else if (pendingChallengeId) {
          // User has accepted disclaimer and consented, proceed with joining
          await joinChallengeInternal(pendingChallengeId, id);
          setPendingChallengeId(null);
        }
      } else {
        alert('Error storing disclaimer acceptance. Please try again.');
      }
    } catch (error) {
      console.error('Error handling disclaimer acceptance:', error);
      alert('Error storing disclaimer acceptance. Please try again.');
    }
  };

  const handleDisclaimerCancel = () => {
    // Just close the modal, nothing changes
    setShowDisclaimerModal(false);
    setAcceptResponsibility(false);
    setDoNotShowAgain(false);
    setPendingChallengeId(null);
  };

  // Handle consent modal actions
  const handleConsent = async () => {
    try {
      // Get userId - use prop if available, otherwise get from auth (getSession to avoid extra GET /auth/v1/user)
      let id = userId;
      if (!id) {
        const { data: { session } } = await supabase.auth.getSession();
        id = session?.user?.id;
        if (!id) {
          setShowConsentModal(false);
          return;
        }
      }

      // Store consent in database
      const consentStored = await storeHealthDataConsent(id);
      
      if (consentStored) {
        setShowConsentModal(false);
        
        // If there's a pending challenge, join it now
        if (pendingChallengeId) {
          await joinChallengeInternal(pendingChallengeId, id);
          setPendingChallengeId(null);
        }
      } else {
        alert('Error storing consent. Please try again.');
      }
    } catch (error) {
      console.error('Error handling consent:', error);
      alert('Error storing consent. Please try again.');
    }
  };

  const handleNoConsent = () => {
    // Just close the modal, nothing changes
    setShowConsentModal(false);
    setPendingChallengeId(null);
  };

  if (loading) {
    return (
      <div className="challenges-tab">
        <div className="challenges-loading">
          <JellyfishLoader />
        </div>
      </div>
    );
  }

  const filteredChallenges = getFilteredChallenges();

  return (
    <div className="challenges-tab">
      <div className="challenges-header">
        <div className="challenges-filter-group">
          <button
            className={`challenges-filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All
          </button>
          <button
            className={`challenges-filter-btn ${filter === 'active' ? 'active' : ''}`}
            onClick={() => setFilter('active')}
          >
            Active
          </button>
          <button
            className={`challenges-filter-btn ${filter === 'completed' ? 'active' : ''}`}
            onClick={() => setFilter('completed')}
          >
            Completed
          </button>
        </div>
      </div>

      <div className="challenges-content">
        {filteredChallenges.length === 0 ? (
          <div className="challenges-empty">
            <h3>No challenges found</h3>
            <p>
              {isOwnProfile
                ? (filter === 'all' 
                    ? 'No challenges available yet. Check back later!'
                    : filter === 'active'
                    ? 'You have no active challenges. Join a challenge to get started!'
                    : 'You have not completed any challenges yet.')
                : (filter === 'all'
                    ? 'This user has not joined any challenges yet.'
                    : filter === 'active'
                    ? 'This user has no active challenges.'
                    : 'This user has not completed any challenges yet.')}
            </p>
          </div>
        ) : (
          <div className="challenges-grid">
            {filteredChallenges.map(challenge => {
              const progress = getUserProgressForChallenge(challenge.id);
              const status = getChallengeStatus(challenge);

              return (
                <ChallengeCard
                  key={challenge.id}
                  challenge={challenge}
                  progress={progress}
                  status={status}
                  onJoin={isOwnProfile ? () => handleJoinChallenge(challenge.id) : undefined}
                  isOwnProfile={isOwnProfile}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Challenge Join Disclaimer Modal */}
      {showDisclaimerModal && (
        <div className="modal-overlay" onClick={(e) => {
          // Don't close on overlay click - user must explicitly choose
          e.stopPropagation();
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>Challenge Join Disclaimer</h3>
            </div>
            <div className="modal-body" style={{ padding: '24px' }}>
              <div style={{ 
                marginBottom: '24px', 
                fontSize: '14px', 
                lineHeight: '1.6', 
                color: 'var(--color-text-primary)',
                whiteSpace: 'pre-line'
              }}>
                {disclaimerText}
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  gap: '12px', 
                  cursor: 'pointer',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  color: 'var(--color-text-primary)'
                }}>
                  <input
                    type="checkbox"
                    checked={acceptResponsibility}
                    onChange={(e) => setAcceptResponsibility(e.target.checked)}
                    style={{ 
                      marginTop: '2px',
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      flexShrink: 0
                    }}
                  />
                  <span>I understand and accept responsibility for joining this challenge</span>
                </label>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  gap: '12px', 
                  cursor: 'pointer',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  color: 'var(--color-text-primary)'
                }}>
                  <input
                    type="checkbox"
                    checked={doNotShowAgain}
                    onChange={(e) => setDoNotShowAgain(e.target.checked)}
                    style={{ 
                      marginTop: '2px',
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      flexShrink: 0
                    }}
                  />
                  <span>Do not show again</span>
                </label>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button
                  type="button"
                  className="btn btn--secondary btn--md btn--rounded"
                  onClick={handleDisclaimerCancel}
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn--primary btn--md btn--rounded"
                  onClick={handleDisclaimerAccept}
                  disabled={!acceptResponsibility}
                  style={{ flex: 1 }}
                >
                  Accept
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Health Data Consent Modal */}
      {showConsentModal && (
        <div className="modal-overlay" onClick={(e) => {
          // Don't close on overlay click - user must explicitly choose
          e.stopPropagation();
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>Health Data Consent</h3>
            </div>
            <div className="modal-body" style={{ padding: '24px' }}>
              <p style={{ marginBottom: '20px', fontSize: '14px', lineHeight: '1.6', color: 'var(--color-text-primary)' }}>
                I explicitly consent to the processing of my health and biometric data, including wearable data and laboratory biomarkers (e.g. hormone levels), to provide personalised health insights and reports.
              </p>
              <p style={{ marginBottom: '20px', fontSize: '14px', lineHeight: '1.6', color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
                You are unable to join challenges without clicking the I consent button.
              </p>
              <p style={{ marginBottom: '20px', fontSize: '14px', lineHeight: '1.6', color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
                You can withdraw this consent at any time in account settings.
              </p>
              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button
                  type="button"
                  className="btn btn--secondary btn--md btn--rounded"
                  onClick={handleNoConsent}
                  style={{ flex: 1 }}
                >
                  I don't consent
                </button>
                <button
                  type="button"
                  className="btn btn--primary btn--md btn--rounded"
                  onClick={handleConsent}
                  style={{ flex: 1 }}
                >
                  I consent
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ChallengeCard({ challenge, progress, status, onJoin, isOwnProfile = true }) {
  const hasJoined = !!progress;
  const isCompleted = progress?.completed || false;
  const progressValue = progress?.progress_value || 0;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const getStatusColor = () => {
    if (isCompleted) return 'rgba(16, 185, 129, 0.12)'; // green
    if (status === 'active') return 'rgba(59, 130, 246, 0.12)'; // blue
    if (status === 'upcoming') return 'rgba(139, 92, 246, 0.12)'; // purple
    return 'rgba(100, 116, 139, 0.12)'; // gray (ended)
  };

  const getStatusLabel = () => {
    if (isCompleted) return 'Completed';
    if (status === 'active') return 'Active';
    if (status === 'upcoming') return 'Upcoming';
    return 'Ended';
  };

  // Calculate progress percentage if there are targets
  const getProgressPercentage = () => {
    if (!challenge.challenge_targets || challenge.challenge_targets.length === 0) {
      return 0;
    }
    
    // For simplicity, using the first target
    const target = challenge.challenge_targets[0];
    if (!target) return 0;
    
    const targetValue = parseFloat(target.target_value);
    if (targetValue === 0) return 0;
    
    return Math.min(100, (progressValue / targetValue) * 100);
  };

  const progressPercentage = getProgressPercentage();
  
  // Get target value for display
  const getTargetValue = () => {
    if (!challenge.challenge_targets || challenge.challenge_targets.length === 0) {
      return 0;
    }
    const target = challenge.challenge_targets[0];
    return target ? parseFloat(target.target_value) : 0;
  };

  const targetValue = getTargetValue();

  return (
    <div className={`challenge-card ${isCompleted ? 'completed' : ''} ${!hasJoined ? 'not-joined' : ''}`}>
      {isCompleted && (
        <div className="challenge-card-completed-cap">
          <Icon name="CheckCircle" className="challenge-completed-cap-icon" />
        </div>
      )}
      <div className="challenge-card-header">
        <h3 className="challenge-card-title">{challenge.title}</h3>
      </div>

      <div className="challenge-card-content">
        <p className="challenge-card-description">
          {challenge.description || 'No description available'}
        </p>

        {hasJoined && (
          <div className="challenge-card-progress">
            <div className="challenge-progress-header">
              <span className="challenge-progress-label">Progress</span>
              <span className="challenge-progress-value">{Math.round(Math.min(progressValue, targetValue))}/{Math.round(targetValue)}</span>
            </div>
            <div className="challenge-progress-bar">
              <div 
                className={`challenge-progress-fill ${isCompleted ? 'completed' : 'in-progress'}`}
                style={{
                  width: `${progressPercentage}%`
                }}
              />
            </div>
          </div>
        )}

        {isOwnProfile && !hasJoined && status === 'active' && (
          <button className="challenge-join-btn" onClick={onJoin}>
            <Icon name="Plus" className="challenge-join-icon" />
            Join Challenge
          </button>
        )}

        {isOwnProfile && !hasJoined && status === 'upcoming' && (
          <button className="challenge-join-btn disabled" disabled>
            Coming Soon
          </button>
        )}
      </div>

      <div className="challenge-card-tags">
        <div className="challenge-card-date">
          <Icon name="Calendar" className="challenge-date-icon" />
          <span>
            {isCompleted && progress?.completed_at 
              ? formatDate(progress.completed_at)
              : `${formatDate(challenge.start_date)} - ${formatDate(challenge.end_date)}`
            }
          </span>
        </div>
        
        {challenge.reward_xp && challenge.reward_xp > 0 && (
          <div className="challenge-card-reward">
            <span>{challenge.reward_xp} XP</span>
          </div>
        )}
        
        {isCompleted ? (
          <div className="challenge-card-status-badge" style={{ backgroundColor: getStatusColor() }}>
            <span>Completed</span>
          </div>
        ) : (
          <div className="challenge-card-status-badge" style={{ backgroundColor: getStatusColor() }}>
            {getStatusLabel()}
          </div>
        )}
      </div>
    </div>
  );
}

export default ChallengesTab;

